import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { CircleCheck } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Button } from '@/components/ui/button';
import { MODAL_TIMEOUT } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useCountdown } from '@/hooks/useCountdown';
import { useSocket } from '@/hooks/useSocket';
import { useWidgetState } from '@/hooks/useWidgetState';
import { chatModalState, isWidgetExpandedState, sessionState, shouldDisplaySurveySelector, } from '@/state/session';
import { sleep } from '@/util/utils';
export const Resolved = () => {
    const { t } = useTranslation();
    const { trackEvent } = useAnalytics();
    const { closeWidget } = useWidgetState();
    const { reopenConversation, closeSession } = useSocket();
    const [session, setSessionState] = useRecoilState(sessionState);
    const keep_alive = session === null || session === void 0 ? void 0 : session.keep_alive;
    const remaining_keep_alive = session === null || session === void 0 ? void 0 : session.remaining_keep_alive;
    const { minutes, seconds } = useCountdown(remaining_keep_alive !== null && remaining_keep_alive !== void 0 ? remaining_keep_alive : keep_alive);
    const shouldDisplaySurvey = useRecoilValue(shouldDisplaySurveySelector);
    const setChatModal = useSetRecoilState(chatModalState);
    const isExpanded = useRecoilValue(isWidgetExpandedState);
    const handleClose = useCallback(async () => {
        setChatModal(null);
        trackEvent(AnalyticsEvent.PopoverResolvedEndChatClicked);
        await sleep(MODAL_TIMEOUT);
        if (shouldDisplaySurvey) {
            setChatModal("survey" /* ChatModal.Survey */);
        }
        else {
            closeSession();
            closeWidget();
        }
    }, [
        closeSession,
        closeWidget,
        setChatModal,
        shouldDisplaySurvey,
        trackEvent,
    ]);
    const handleCancel = useCallback(() => {
        trackEvent(AnalyticsEvent.PopoverResolvedContinueClicked);
        reopenConversation();
        setChatModal(null);
    }, [reopenConversation, setChatModal, trackEvent]);
    useEffect(() => {
        (async () => {
            if (minutes === 0 && seconds === 0) {
                setChatModal(null);
                await sleep(MODAL_TIMEOUT);
                setSessionState((prev) => (Object.assign(Object.assign({}, prev), { status: "expired" /* SessionStatus.Expired */ })));
                setChatModal("expired" /* ChatModal.Expired */);
            }
        })();
    }, [minutes, seconds, setChatModal, setSessionState]);
    return (_jsxs("div", { className: "flex h-full flex-col items-center gap-4 p-4 text-center", children: [_jsx(CircleCheck, { size: 40, color: "var(--color-successful)" }), _jsxs("div", { className: "text-foreground text-lg leading-none font-semibold", children: [t('modal.resolved'), _jsx("p", { className: "text-foreground-muted mx-auto mt-1 text-sm leading-5 font-normal", children: t('resolved_continue') })] }), _jsx("div", { className: "flex w-full flex-col items-center justify-center", children: _jsxs("div", { className: "grid min-h-8 w-fit grid-cols-[36px_16px_36px] items-center", children: [_jsxs("span", { className: "text-foreground-muted text-2xl leading-8", children: [minutes && minutes < 10 && '0', minutes] }), _jsx("span", { className: "text-foreground-muted -translate-y-0.5 text-2xl leading-8", children: ":" }), _jsxs("span", { className: "text-foreground-muted text-2xl leading-8", children: [seconds && seconds < 10 && '0', seconds] })] }) }), _jsxs("div", { className: "flex w-full flex-col items-center justify-center gap-2", children: [_jsx(Button, { onClick: handleClose, className: cn('w-full', {
                            'w-sm': isExpanded,
                        }), children: t('buttons.end_conversation') }), _jsx(Button, { onClick: handleCancel, variant: "secondary", className: cn('w-full', {
                            'w-sm': isExpanded,
                        }), children: t('buttons.resume') })] })] }));
};
